import React from 'react';
import { API } from '../api';
import { fmtTime12, fmtDate, ttLabel } from '../utils/format';
import TrophySvg from './TrophySvg';

function CompetitionFixtures({ addManualMatch, clearScore, completedMatches, deleteDraw, deleteMatch, fixtureMode, generateFinals, generateFixtures, handleCompTeamClick, handleManualDrawClick, isAdmin, isFinalised, isLocked, isReopened, manualMatch, manualTimeLocked, matches, selectedComp, setActiveStep, setComps, setFixtureMode, setManualMatch, setScoringMatch, setSelectedComp, showConfirm, teams }) {
  return (
    <div className="w3-card w3-round w3-white w3-margin-top">
      <div className="w3-container w3-padding">
        <h4><i className="fa fa-list w3-text-theme w3-margin-right"></i>
          {ttLabel(selectedComp.tournamentType || 'round-robin')} Draw
        </h4>

        {isAdmin && !isFinalised && !isReopened && (
          <label className="w3-small" style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', cursor: 'pointer' }}>
            <input type="checkbox" className="w3-check"
              checked={!!selectedComp.drawFinalised}
              onChange={async e => {
                const val = e.target.checked;
                try {
                  const res = await fetch(`${API}/api/competitions/${selectedComp.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ drawFinalised: val }),
                  });
                  if (res.ok) {
                    setSelectedComp(prev => ({ ...prev, drawFinalised: val }));
                    setComps(prev => prev.map(c => c.id === selectedComp.id ? { ...c, drawFinalised: val } : c));
                  }
                } catch {}
              }}
            />
            Draw finalised
          </label>
        )}

        {isLocked && (
          <div className="w3-panel w3-pale-yellow w3-leftbar w3-border-amber w3-round" style={{ padding: '8px 12px', marginBottom: '12px' }}>
            <i className="fa fa-lock w3-margin-right"></i>
            <strong className="w3-small">{selectedComp.drawFinalised && !isFinalised && !isReopened ? 'Draw finalised' : `Competition ${isFinalised ? 'finalised' : 'reopened for score fixes only'}`}</strong>
            <span className="w3-small"> — {isFinalised ? 'reopen from the Results tab to make changes' : selectedComp.drawFinalised && !isFinalised && !isReopened ? 'untick to make changes' : 'draw is locked'}</span>
          </div>
        )}

        {/* Auto / Manual toggle + action buttons */}
        {!isLocked && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '0' }}>
              <button className={`w3-button w3-round-left w3-small ${fixtureMode === 'auto' ? 'w3-theme' : 'w3-theme-l4'}`}
                onClick={() => setFixtureMode('auto')} style={{ borderRight: '1px solid rgba(0,0,0,0.1)' }}>
                <i className={`fa ${selectedComp.drawMethod === 'rating-balanced' ? 'fa-balance-scale' : 'fa-random'} w3-margin-right`}></i>
                Auto {selectedComp.drawMethod === 'rating-balanced' ? '(Rated)' : '(Random)'}
              </button>
              <button className={`w3-button w3-round-right w3-small ${fixtureMode === 'manual' ? 'w3-theme' : 'w3-theme-l4'}`}
                onClick={handleManualDrawClick}>
                <i className="fa fa-pencil-square-o w3-margin-right"></i>Manual Draw
              </button>
            </div>
            {matches.length > 0 && fixtureMode === 'auto' && (
              <button className="w3-button w3-theme-l4 w3-round w3-small" onClick={generateFixtures}>
                <i className="fa fa-refresh w3-margin-right"></i>Re-generate
              </button>
            )}
            {matches.length > 0 && (
              <button className="w3-button w3-pale-red w3-round w3-small" onClick={deleteDraw}>
                <i className="fa fa-trash w3-margin-right"></i>Delete Draw
              </button>
            )}
              {selectedComp.hasFinalsSeries && selectedComp.tournamentType === 'round-robin' && matches.filter(m => !m.bracketType || m.bracketType !== 'finals').length > 0 && (
                <button className="w3-button w3-theme w3-round w3-small" onClick={generateFinals}>
                  <i className="fa fa-trophy w3-margin-right"></i>
                  {matches.some(m => m.bracketType === 'finals') ? 'Re-generate Finals' : 'Generate Finals'}
                </button>
              )}
          </div>
        )}

        {/* === AUTO MODE === */}
        {fixtureMode === 'auto' && matches.length === 0 && !isLocked && (
          <div className="w3-center w3-padding-large">
            <p className="w3-text-grey">No fixtures generated yet</p>
            {selectedComp.drawMethod === 'rating-balanced' && (
              <p className="w3-small w3-text-blue" style={{ marginTop: 0 }}>
                <i className="fa fa-balance-scale w3-margin-right"></i>
                Rating-balanced draw — players matched by skill level
              </p>
            )}
            <button className="w3-button w3-theme w3-round-large w3-large" onClick={generateFixtures}>
              <i className={`fa ${selectedComp.drawMethod === 'rating-balanced' ? 'fa-balance-scale' : 'fa-random'} w3-margin-right`}></i>
              Generate Fixtures
            </button>
          </div>
        )}

        {/* === MANUAL MODE form === */}
        {fixtureMode === 'manual' && !isLocked && (
          <div className="w3-card w3-round w3-pale-blue w3-padding" style={{ marginBottom: '16px' }}>
            <h5 style={{ marginTop: 0 }}><i className="fa fa-plus-circle w3-margin-right"></i>Add Match</h5>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-end' }}>
              <div style={{ flex: '0 0 60px' }}>
                <label className="w3-tiny" style={{ display: 'block' }}>Round</label>
                <input type="number" min="1" className="w3-input w3-border w3-round w3-small"
                  value={manualMatch.round}
                  onChange={e => setManualMatch(p => ({ ...p, round: parseInt(e.target.value) || 1 }))} />
              </div>
              <div style={{ flex: '1 1 140px' }}>
                <label className="w3-tiny" style={{ display: 'block' }}>Home Team</label>
                <select className="w3-select w3-border w3-round w3-small" value={manualMatch.homeTeamId}
                  onChange={e => setManualMatch(p => ({ ...p, homeTeamId: e.target.value }))}>
                  <option value="">— Select —</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div style={{ flex: '0 0 auto', alignSelf: 'center', paddingTop: '14px' }}>
                <span className="w3-text-grey w3-small"><strong>vs</strong></span>
              </div>
              <div style={{ flex: '1 1 140px' }}>
                <label className="w3-tiny" style={{ display: 'block' }}>Away Team</label>
                <select className="w3-select w3-border w3-round w3-small" value={manualMatch.awayTeamId}
                  onChange={e => setManualMatch(p => ({ ...p, awayTeamId: e.target.value }))}>
                  <option value="">— Select —</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div style={{ flex: '0 0 60px' }}>
                <label className="w3-tiny" style={{ display: 'block' }}>Green</label>
                <input type="number" min="1" className="w3-input w3-border w3-round w3-small"
                  value={manualMatch.green}
                  onChange={e => setManualMatch(p => ({ ...p, green: e.target.value }))} />
              </div>
              <div style={{ flex: '0 0 60px' }}>
                <label className="w3-tiny" style={{ display: 'block' }}>Rink</label>
                <input type="number" min="1" className="w3-input w3-border w3-round w3-small"
                  value={manualMatch.rink}
                  onChange={e => setManualMatch(p => ({ ...p, rink: e.target.value }))} />
              </div>
              <div style={{ flex: '0 0 90px' }}>
                <label className="w3-tiny" style={{ display: 'block' }}>Time</label>
                <input type="time" className="w3-input w3-border w3-round w3-small"
                  value={manualMatch.time}
                  onChange={e => setManualMatch(p => ({ ...p, time: e.target.value }))}
                  disabled={manualTimeLocked}
                />
                {manualTimeLocked && (
                  <span className="w3-tiny w3-text-grey">Time set by other matches in this round</span>
                )}
              </div>
              <div style={{ flex: '0 0 auto', paddingTop: '14px' }}>
                <button className="w3-button w3-theme w3-round w3-small" onClick={addManualMatch}>
                  <i className="fa fa-plus w3-margin-right"></i>Add
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Empty state for manual mode */}
        {fixtureMode === 'manual' && matches.length === 0 && (
          <div className="w3-center w3-padding" style={{ marginBottom: '8px' }}>
            <p className="w3-text-grey"><em>No matches added yet. Use the form above to add matches.</em></p>
          </div>
        )}

        {matches.length > 0 && (
          <>
            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span className="w3-small w3-text-grey">
                {matches.length} matches &bull; {completedMatches} scored
              </span>
            </div>

            {(() => {
              const tt = selectedComp.tournamentType || 'round-robin';
              const isDE = tt === 'double-elimination';

              /* Helper: render a table of matches */
              const renderMatchTable = (matchList, label) => {
                if (!matchList.length) return null;
                const rounds = [...new Set(matchList.map(m => m.round))].sort((a, b) => a - b);
                return rounds.map(round => {
                  const roundMatch = matchList.find(m => m.round === round);
                  const roundMatches = matchList.filter(m => m.round === round);
                  const isFinalsRound = roundMatches.length > 0 && roundMatches.every(m => m.bracketType === 'finals');
                  const totalFinalsRounds = isFinalsRound
                    ? Math.max(...matchList.filter(m => m.bracketType === 'finals').map(m => m.bracketRound || 1))
                    : 0;
                  const currentFinalsRound = isFinalsRound ? (roundMatches[0].bracketRound || 1) : 0;
                  const finalsStageLabel = !isFinalsRound ? ''
                    : currentFinalsRound === totalFinalsRounds ? 'Final'
                    : currentFinalsRound === (totalFinalsRounds - 1) ? 'Semi Final'
                    : currentFinalsRound === (totalFinalsRounds - 2) ? 'Quarter Final'
                    : 'Finals';
                  const compDates = selectedComp.roundStartDates || {};
                  const compTimes = selectedComp.roundStartTimes || {};
                  const roundDate = (roundMatch && roundMatch.date) || compDates[String(round)] || selectedComp.startDate || '';
                  const roundTime = (roundMatch && roundMatch.time) || compTimes[String(round)] || '';
                  const overallRound = isDE && roundMatches.length
                    ? Math.min(...roundMatches.map(m => m.overallRound || m.round || round))
                    : round;
                  const roundInfo = [roundDate ? fmtDate(roundDate) : '', roundTime ? fmtTime12(roundTime) : ''].filter(Boolean).join(' · ');
                  return (
                  <div key={`${label}-${round}`} className="w3-margin-bottom">
                    <h5 className="w3-theme-l3 w3-padding-small w3-round" style={{ marginBottom: '4px', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                      <span>{`Round ${round}${isDE && overallRound !== round ? ` (Overall Round ${overallRound})` : ''}${finalsStageLabel ? ` - ${finalsStageLabel}` : ''}`}</span>
                      {roundInfo && <span className="w3-small w3-text-grey" style={{ fontWeight: 'normal', fontSize: 'var(--gs-text-sm)' }}>{roundInfo}</span>}
                    </h5>
                    <div className="w3-responsive">
                      <table className="w3-table w3-striped w3-bordered w3-small">
                        <thead className="w3-theme-d5">
                          <tr><th className="w3-center">Rink</th><th>Home</th><th className="w3-center">Score</th><th>Away</th><th className="w3-center">Score</th><th>Status</th><th></th></tr>
                        </thead>
                        <tbody>
                          {matchList.filter(m => m.round === round).map(m => {
                            const hName = m.HomeTeam ? m.HomeTeam.name : (m.homeTeamId ? 'TBD' : 'BYE');
                            const aName = m.AwayTeam ? m.AwayTeam.name : (m.awayTeamId ? 'TBD' : (isDE && !m.homeTeamId ? 'TBD' : 'BYE'));
                            const waiting = isDE && (!m.homeTeamId || !m.awayTeamId) && m.result !== 'bye';
                            const oneTeamWaiting = waiting && !!(m.homeTeamId || m.awayTeamId);
                            return (
                              <tr key={m.id} className={m.completed && m.result !== 'bye' && m.result !== 'not-played' ? 'w3-pale-green' : waiting ? 'w3-pale-blue' : ''}>
                                <td className="w3-center">{m.rink || '-'}</td>
                                <td className={m.result === 'win-home' ? 'w3-text-green' : ''}>
                                  <strong style={m.homeTeamId ? { cursor: 'pointer' } : {}} onClick={(e) => m.homeTeamId && handleCompTeamClick(m.homeTeamId, e)}>{hName}</strong>
                                </td>
                                <td className="w3-center"><strong>{m.completed ? m.homeScore : '-'}</strong></td>
                                <td className={m.result === 'win-away' ? 'w3-text-green' : ''}>
                                  <strong style={m.awayTeamId ? { cursor: 'pointer' } : {}} onClick={(e) => m.awayTeamId && handleCompTeamClick(m.awayTeamId, e)}>{aName}</strong>
                                </td>
                                <td className="w3-center"><strong>{m.completed ? m.awayScore : '-'}</strong></td>
                                <td>
                                  {waiting ? <span className="w3-tag w3-light-blue w3-round w3-small">{oneTeamWaiting ? 'Bye / Waiting' : 'Waiting'}</span>
                                    : m.result === 'bye' ? <span className="w3-tag w3-light-grey w3-round w3-small">BYE</span>
                                    : m.result === 'not-played' ? (
                                      <span className={`w3-tag w3-orange w3-round w3-small${isAdmin && !isFinalised ? ' w3-hover-shadow' : ''}`}
                                        style={isAdmin && !isFinalised ? { cursor: 'pointer' } : {}}
                                        title={isAdmin && !isFinalised ? 'Click to reset to Pending' : ''}
                                        onClick={() => isAdmin && !isFinalised && showConfirm('Reset this match to Pending?').then(ok => ok && clearScore(m.id))}
                                      >Not Played</span>
                                    ) : (m.completed && (m.homeScore === 0 && m.awayScore === 0) && (m.result === 'win-home' || m.result === 'win-away'))
                                      ? (
                                      <span className={`w3-tag w3-deep-orange w3-round w3-small${isAdmin && !isFinalised ? ' w3-hover-shadow' : ''}`}
                                        style={isAdmin && !isFinalised ? { cursor: 'pointer' } : {}}
                                        title={isAdmin && !isFinalised ? 'Click to reset to Pending' : ''}
                                        onClick={() => isAdmin && !isFinalised && showConfirm('Reset this match to Pending?').then(ok => ok && clearScore(m.id))}
                                      >Forfeit</span>
                                    ) : m.completed ? (
                                      <span className={`w3-tag w3-green w3-round w3-small${isAdmin && !isFinalised ? ' w3-hover-shadow' : ''}`}
                                        style={isAdmin && !isFinalised ? { cursor: 'pointer' } : {}}
                                        title={isAdmin && !isFinalised ? 'Click to reset to Pending' : ''}
                                        onClick={() => isAdmin && !isFinalised && showConfirm('Reset this match to Pending?').then(ok => ok && clearScore(m.id))}
                                      >Done</span>
                                    ) : <span className="w3-tag w3-amber w3-round w3-small">Pending</span>}
                                </td>
                                <td style={{ whiteSpace: 'nowrap' }}>
                                  {isAdmin && m.result !== 'bye' && !isFinalised && !waiting && (
                                    <button className="w3-button w3-tiny w3-theme w3-round"
                                      onClick={() => setScoringMatch(m)}>
                                      <i className="fa fa-pencil"></i> {m.completed ? 'Edit' : 'Score'}
                                    </button>
                                  )}
                                  {isAdmin && !isLocked && !m.completed && m.result !== 'bye' && (
                                    <button className="w3-button w3-tiny w3-red w3-round w3-margin-left"
                                      onClick={() => deleteMatch(m.id)} title="Delete match">
                                      <i className="fa fa-trash"></i>
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );});
              };

              if (isDE) {
                // Normalise: use bracketRound for grouping/labelling within each bracket.
                // Falls back to global round for old draws that predate bracketRound.
                const withBR = (list) => list.map(m => ({
                  ...m,
                  overallRound: m.round,
                  round: m.bracketRound != null ? m.bracketRound : m.round,
                }));

                const winners = withBR(matches.filter(m => (m.bracketType === 'winners' || !m.bracketType) && (m.homeTeamId || m.awayTeamId || !m.completed)));
                const losers  = withBR(matches.filter(m => m.bracketType === 'losers'  && (m.homeTeamId || m.awayTeamId || !m.completed)));
                const gf      = withBR(matches.filter(m => m.bracketType === 'grand-final'));
                const gfDone  = gf.every(m => m.completed);
                const decider = withBR(matches.filter(m => m.bracketType === 'decider' && m.result !== 'not-played'));
                const showDecider = gfDone && decider.length > 0;

                // Count who's been eliminated (lost twice in DE)
                const lossCount = {};
                matches.forEach(m => {
                  if (!m.completed || m.result === 'bye' || m.result === 'not-played') return;
                  const loserId = m.result === 'win-home' ? m.awayTeamId : m.result === 'win-away' ? m.homeTeamId : null;
                  if (loserId) lossCount[loserId] = (lossCount[loserId] || 0) + 1;
                });
                const eliminatedTeamIds = new Set(Object.keys(lossCount).filter(id => lossCount[id] >= 2));
                const totalTeams = matches.reduce((ids, m) => {
                  if (m.homeTeamId) ids.add(m.homeTeamId);
                  if (m.awayTeamId) ids.add(m.awayTeamId);
                  return ids;
                }, new Set()).size;
                const teamsRemaining = Math.max(totalTeams - eliminatedTeamIds.size, 0);
                const winnerCrowned = totalTeams > 1 && teamsRemaining <= 1;

                return (
                  <>
                    {/* Format legend */}
                    <div className="w3-panel w3-pale-blue w3-round w3-small" style={{ padding: '8px 12px', marginBottom: '12px' }}>
                      <i className="fa fa-info-circle w3-margin-right w3-text-blue"></i>
                      <strong>Double Elimination:</strong> Win → stay in Winners Bracket &nbsp;|&nbsp; Lose once → drop to Losers Bracket &nbsp;|&nbsp; Lose twice → eliminated
                      {winnerCrowned
                        ? <span className="w3-margin-left w3-text-green"><strong>All available opponents eliminated — winner crowned</strong></span>
                        : (eliminatedTeamIds.size > 0 && <span className="w3-margin-left w3-text-red"><strong>{eliminatedTeamIds.size}</strong> of {totalTeams} eliminated</span>)}
                    </div>

                    {/* ── WINNERS BRACKET ── */}
                    <div style={{ background: '#f0f8ff', border: '2px solid #0078d4', borderRadius: '8px', padding: '12px', marginBottom: '0' }}>
                      <h5 style={{ margin: '0 0 10px 0', color: '#0056a8', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <TrophySvg size={14} color="#0056a8" /> Winners Bracket
                        <span className="w3-small" style={{ fontWeight: 'normal', color: '#555' }}>(win to stay here)</span>
                      </h5>
                      {renderMatchTable(winners, 'W')}
                    </div>

                    {/* ── DIVIDER ── */}
                    <div style={{ display: 'flex', alignItems: 'center', margin: '10px 0', gap: '8px' }}>
                      <div style={{ flex: 1, borderTop: '2px dashed #e07000' }} />
                      <span style={{ color: '#e07000', fontWeight: 700, fontSize: 'var(--gs-text-sm)', whiteSpace: 'nowrap' }}>
                        <i className="fa fa-arrow-down w3-margin-right"></i>Losers drop below this line<i className="fa fa-arrow-down w3-margin-left"></i>
                      </span>
                      <div style={{ flex: 1, borderTop: '2px dashed #e07000' }} />
                    </div>

                    {/* ── LOSERS BRACKET ── */}
                    {losers.length > 0 && (
                      <div style={{ background: '#fff8f0', border: '2px solid #e07000', borderRadius: '8px', padding: '12px', marginBottom: '0' }}>
                        <h5 style={{ margin: '0 0 10px 0', color: '#b85000', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <i className="fa fa-refresh" style={{ color: '#b85000' }}></i> Losers Bracket
                          <span className="w3-small" style={{ fontWeight: 'normal', color: '#555' }}>(lose here = eliminated)</span>
                        </h5>
                        {renderMatchTable(losers, 'L')}
                      </div>
                    )}

                    {/* ── GRAND FINAL ── */}
                    <div style={{ background: '#f0fff0', border: '2px solid #2e7d32', borderRadius: '8px', padding: '12px', marginTop: '10px' }}>
                      <h5 style={{ margin: '0 0 10px 0', color: '#2e7d32', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="fa fa-star" style={{ color: '#2e7d32' }}></i> Grand Final
                        <span className="w3-small" style={{ fontWeight: 'normal', color: '#555' }}>(Winners champ vs Losers survivor)</span>
                      </h5>
                      {renderMatchTable(gf, 'GF')}
                    </div>

                    {/* ── DECIDER (only if losers finalist wins Grand Final) ── */}
                    {showDecider && (
                      <div style={{ background: '#fdf0ff', border: '2px solid #7b1fa2', borderRadius: '8px', padding: '12px', marginTop: '10px' }}>
                        <h5 style={{ margin: '0 0 10px 0', color: '#7b1fa2', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <i className="fa fa-bolt" style={{ color: '#7b1fa2' }}></i> Decider
                          <span className="w3-small" style={{ fontWeight: 'normal', color: '#555' }}>(both players now have 1 loss)</span>
                        </h5>
                        {renderMatchTable(decider, 'D')}
                      </div>
                    )}
                  </>
                );
              }

              /* Standard round-robin / knockout rendering by round */
              const finalsMatches = matches.filter(m => m.bracketType === 'finals');
              const rrMatchesList = matches.filter(m => m.bracketType !== 'finals');
              const finalsRoundLabel = (br, totalFR) => {
                if (br === totalFR) return 'Final';
                if (br === totalFR - 1) return 'Semi-Final';
                if (br === totalFR - 2) return 'Quarter-Final';
                return `Round ${br}`;
              };
              const totalFinalsRounds = finalsMatches.length > 0
                ? Math.max(...finalsMatches.map(m => m.bracketRound || 1))
                : 0;

              return (
                <>
                  {renderMatchTable(rrMatchesList, '')}
                  {finalsMatches.length > 0 && (
                    <div style={{ background: '#fffbf0', border: '2px solid #c8a000', borderRadius: '8px', padding: '12px', marginTop: '16px' }}>
                      <h5 style={{ margin: '0 0 4px 0', color: '#7a5c00', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="fa fa-trophy" style={{ color: '#c8a000' }}></i> Tie-Break Finals
                      </h5>
                      {!selectedComp.hasFinalsSeries && (
                        <p style={{ margin: '0 0 12px 0', fontSize: 'var(--gs-text-sm)', color: '#5a4200' }}>
                          <i className="fa fa-info-circle" style={{ marginRight: 5 }}></i>
                          Two or more teams finished level on points, so these play-off matches were added automatically to find a winner.
                          {totalFinalsRounds > 1
                            ? ' Seeding is based on head-to-head results from the round-robin (who beat who among the tied teams), then points difference. The top-seeded tied team goes straight to the Final — the others play a Semi-Final first.'
                            : ' The top two tied teams play a Final to decide the champion, seeded by their head-to-head result from the round-robin.'}
                        </p>
                      )}
                      {Array.from(new Set(finalsMatches.map(m => m.bracketRound || 1)))
                        .sort((a, b) => a - b)
                        .map(br => {
                          const roundMs = finalsMatches
                            .filter(m => (m.bracketRound || 1) === br)
                            .map(m => ({ ...m, round: br }));
                          return (
                            <div key={br} style={{ marginBottom: '12px' }}>
                              <div style={{ fontWeight: 700, color: '#7a5c00', marginBottom: '6px', fontSize: 'var(--gs-text-sm)' }}>
                                {finalsRoundLabel(br, totalFinalsRounds)}
                              </div>
                              {renderMatchTable(roundMs, finalsRoundLabel(br, totalFinalsRounds).charAt(0))}
                            </div>
                          );
                        })}
                    </div>
                  )}
                </>
              );
            })()}
          </>
        )}

        {/* Nav */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', paddingBottom: '4px' }}>
          <button className="w3-button w3-theme-l4 w3-round" onClick={() => setActiveStep(selectedComp.format === 'singles' ? 'players' : 'teams')}>
            <i className="fa fa-arrow-left w3-margin-right"></i>Back
          </button>
          <button className="w3-button w3-theme w3-round-large"
            onClick={() => setActiveStep('scores')} disabled={matches.length === 0}>
            Next: Enter Scores <i className="fa fa-arrow-right w3-margin-left"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

export default CompetitionFixtures;
